import React from "react";

import { eventDetailText } from "@/components/telemetry/EventsTable";
import {
  formatSeconds,
  formatTs,
  toIsoZ,
  type TelemetryTz,
} from "@/components/telemetry/format";
import { formatInstant } from "@voltie/localization-web";
import { useCatalog, type Translate } from "@/app/localization";
import { viewerTimeZone } from "@/app/displayFormat";
import type {
  GapDetail,
  SeriesPoint,
  TelemetryEvent,
  TelemetryEventKind,
} from "@/lib/telemetry";

/**
 * A time map of one window, drawn entirely from data already on screen.
 *
 * ONE scale, not a stack of them. What support is reading here is a sequence —
 * this state, then the gap that swallowed it, then the anomaly inside it — and
 * a sequence read across three parallel rows is a sequence the eye has to
 * reassemble. States are the bands; the events that interrupt them are ticks
 * standing on those bands. Archive completeness is the coverage strip's job,
 * directly above.
 *
 * Nothing here queries anything. The series and events were paid for once by
 * the window scan; laying them out on a shared x-axis is a `useMemo`, and a
 * click only ever changes the URL range (which is what buys the next scan) or
 * opens the drawer over a row already in memory.
 *
 * TELEMETRY, NOT BILLING. Every band is what the charger reported, not what a
 * session recorded: "charging" is the reported state, not a billable
 * subsession; "plugged in" is a connected state with no charging, not a
 * billable idle interval; a gap is missing telemetry, not a finalized offline
 * session end. Sessions and subsessions live in Postgres and are a different
 * source — wiring them in is deliberately a later phase, because a row labelled
 * "session" has to come from the session row or not exist at all.
 */

export type TimelineStateFamily =
  | "charging"
  | "plugged"
  | "standby"
  | "blocked"
  | "error"
  | "offline"
  | "unknown";

/** What a segment was built from; the view turns it into its tooltip line. */
export type TimelineSegmentDetail =
  | { kind: "state"; buckets: number; samples: number; states: string[] }
  | { kind: "gap"; seconds: number; offlineThresholdS: number };

export interface TimelineSegment {
  key: string;
  family: TimelineStateFamily;
  /** ISO bounds, clamped to the window — what a click narrows the range to. */
  from: string;
  to: string;
  startMs: number;
  endMs: number;
  detail: TimelineSegmentDetail;
}

export interface TimelineMarker {
  key: string;
  kind: TelemetryEventKind;
  /** anomalies split by their own severity; everything else uses its kind. */
  tone: string;
  tsMs: number;
  event: TelemetryEvent;
}

/** Charger state names (protocol.STATE_NAMES) collapsed into what support reads. */
const STATE_FAMILY: Record<string, TimelineStateFamily> = {
  charging: "charging",
  charging_simple: "charging",
  connected: "plugged",
  ready: "plugged",
  standby: "standby",
  blocked: "blocked",
  error: "error",
};

export function stateFamilyOf(state: string | null | undefined): TimelineStateFamily {
  if (!state) return "unknown";
  return STATE_FAMILY[state] ?? "unknown";
}

const FAMILY_STYLE: Record<TimelineStateFamily, { block: string; text: string }> = {
  charging: { block: "bg-emerald-500/85", text: "text-white" },
  plugged: { block: "bg-sky-500/80", text: "text-white" },
  standby: { block: "bg-slate-400/50", text: "text-foreground/80" },
  blocked: { block: "bg-violet-500/70", text: "text-white" },
  error: { block: "bg-destructive/75", text: "text-white" },
  offline: {
    block:
      "bg-amber-600/80 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(0,0,0,.22)_3px,rgba(0,0,0,.22)_6px)]",
    text: "text-white",
  },
  unknown: { block: "bg-muted-foreground/25", text: "text-foreground/80" },
};

/** Legend and band label of a state family (operator.telemetry.family). */
export function familyLabel(t: Translate, family: TimelineStateFamily): string {
  return t("operator.telemetry.family", { family });
}

/**
 * Paint order for the bands, which is NOT their DOM order.
 *
 * A gap band has to cover the state buckets that ran into it — the charger
 * reported one thing and then stopped reporting, and the band that says "and
 * then nothing" is the truer one at that instant. That is a stacking question,
 * so it is answered with `z-index` and the DOM stays in time order.
 */
const BAND_LAYER: Record<TimelineStateFamily, number> = {
  charging: 1,
  plugged: 1,
  standby: 1,
  blocked: 1,
  error: 1,
  unknown: 1,
  offline: 5,
};

/**
 * Ticks that have to survive being drawn on top of a coloured band.
 *
 * Every marker carries a background-coloured ring so it separates from whatever
 * state it lands on, and the loudness is ordered by what an operator needs to
 * see first: anomalies are wider, saturated and stacked highest (`layer` → the
 * inline z-index, never the DOM order); a gap stays a hairline, because the
 * offline band underneath it already says the same thing at full width.
 */
const MARKER_STYLE: Record<
  string,
  { block: string; width: string; layer: number }
> = {
  gap: { block: "bg-amber-700 ring-1 ring-background", width: "w-[2px]", layer: 2 },
  anomaly_warn: {
    block: "bg-amber-400 ring-1 ring-background",
    width: "w-[4px]",
    layer: 3,
  },
  anomaly_error: {
    block: "bg-red-600 ring-1 ring-background",
    width: "w-[4px]",
    layer: 3,
  },
};

/**
 * Only what interrupts the picture earns a tick.
 *
 * Samples are rows, not events — thousands of tick marks would say nothing. A
 * state change is already drawn: it is the boundary where one band's colour
 * becomes the next one's, and a tick at every transition is a second copy of
 * that. Commands are worse — the archive's `command` column holds the server's
 * reply to essentially every frame, so a row of command ticks is a row of
 * marks for nothing having happened, densest exactly where the newest-first
 * events page lands. Both stay in the events table and the drawer, where a row
 * costs nothing to skip.
 */
const MARKER_KINDS: TelemetryEventKind[] = ["gap", "anomaly"];
/** Past this the row is a solid bar of ticks; the overflow is reported, never silently dropped. */
const MAX_MARKERS = 600;
/** A one-bucket click must still be a range the API accepts. */
const MIN_RANGE_MS = 60_000;

/* ------------------------------------------------------------------ */
/*  Builders — pure, and the part worth testing                        */
/* ------------------------------------------------------------------ */

interface TimelineWindow {
  fromMs: number;
  toMs: number;
}

function clamp(
  startMs: number,
  endMs: number,
  win: TimelineWindow,
): { startMs: number; endMs: number } | null {
  const start = Math.max(startMs, win.fromMs);
  const end = Math.min(endMs, win.toMs);
  if (!isFinite(start) || !isFinite(end) || end <= start) return null;
  return { startMs: start, endMs: end };
}

/**
 * Bucket width, preferred from the backend's own `meta.bucket_seconds`.
 *
 * A series point is a bucket, not an instant, so its band has to be as wide as
 * the bucket or the row draws gaps that are not in the data. Split-mode
 * responses do not always carry the width; the smallest positive step between
 * adjacent points is that width whenever two buckets are contiguous, and two
 * contiguous buckets is the case that makes the row look wrong.
 */
export function resolveBucketMs(tsMs: number[], bucketSeconds?: number): number {
  if (bucketSeconds && bucketSeconds > 0) return bucketSeconds * 1000;
  let smallest = Infinity;
  for (let i = 1; i < tsMs.length; i += 1) {
    const step = tsMs[i] - tsMs[i - 1];
    if (step > 0 && step < smallest) smallest = step;
  }
  return isFinite(smallest) ? smallest : 60_000;
}

/**
 * Contiguous runs of one state, merged across buckets.
 *
 * A run breaks on a different state OR on a hole — a missing bucket means no
 * samples were archived there, and bridging it would draw a charging band over
 * a window in which the charger said nothing.
 */
export function buildStateSegments(
  series: SeriesPoint[],
  opts: { fromMs: number; toMs: number; bucketSeconds?: number },
): TimelineSegment[] {
  const win = { fromMs: opts.fromMs, toMs: opts.toMs };
  const points = series
    .map((point) => ({
      ms: new Date(point.ts).getTime(),
      family: stateFamilyOf(point.state_dominant),
      state: point.state_dominant,
      samples: point.samples ?? 0,
    }))
    .filter((point) => isFinite(point.ms))
    .sort((a, b) => a.ms - b.ms);
  if (!points.length) return [];

  const bucketMs = resolveBucketMs(points.map((p) => p.ms), opts.bucketSeconds);
  const runs: Array<{
    family: TimelineStateFamily;
    states: Set<string>;
    startMs: number;
    endMs: number;
    buckets: number;
    samples: number;
  }> = [];

  for (const point of points) {
    const open = runs[runs.length - 1];
    const contiguous = open && point.ms - open.endMs <= bucketMs * 0.5;
    if (open && open.family === point.family && contiguous) {
      open.endMs = Math.max(open.endMs, point.ms + bucketMs);
      open.buckets += 1;
      open.samples += point.samples;
      if (point.state) open.states.add(point.state);
      continue;
    }
    runs.push({
      family: point.family,
      states: new Set(point.state ? [point.state] : []),
      startMs: point.ms,
      endMs: point.ms + bucketMs,
      buckets: 1,
      samples: point.samples,
    });
  }

  const out: TimelineSegment[] = [];
  for (const run of runs) {
    const bounds = clamp(run.startMs, run.endMs, win);
    if (!bounds) continue;
    const states = [...run.states];
    out.push({
      key: `state-${run.startMs}-${run.family}`,
      family: run.family,
      from: toIsoZ(new Date(bounds.startMs)),
      to: toIsoZ(new Date(bounds.endMs)),
      startMs: bounds.startMs,
      endMs: bounds.endMs,
      detail: { kind: "state", buckets: run.buckets, samples: run.samples, states },
    });
  }
  return out;
}

/**
 * Offline-looking gaps as bands.
 *
 * Only `offline_looking` gets a band: it is the one the backend already
 * measured against the platform's own offline rule. A short gap stays a tick,
 * because a band would read as "the charger was down" for something well inside
 * normal jitter.
 */
export function buildGapSegments(
  events: TelemetryEvent[],
  opts: { fromMs: number; toMs: number },
): TimelineSegment[] {
  const win = { fromMs: opts.fromMs, toMs: opts.toMs };
  const out: TimelineSegment[] = [];

  for (const event of events) {
    if (event.kind !== "gap") continue;
    const detail = event.detail as GapDetail | null;
    if (!detail || detail.severity !== "offline_looking") continue;

    const endMs = new Date(event.ts).getTime();
    const previous = detail.prev_ts ? new Date(detail.prev_ts).getTime() : NaN;
    const startMs = isFinite(previous) ? previous : endMs - (detail.seconds ?? 0) * 1000;
    const bounds = clamp(startMs, endMs, win);
    if (!bounds) continue;

    out.push({
      key: `gap-${event.ts}`,
      family: "offline",
      from: toIsoZ(new Date(bounds.startMs)),
      to: toIsoZ(new Date(bounds.endMs)),
      startMs: bounds.startMs,
      endMs: bounds.endMs,
      detail: { kind: "gap", seconds: detail.seconds, offlineThresholdS: detail.offline_threshold_s },
    });
  }
  return out;
}

export function markerTone(event: TelemetryEvent): string {
  if (event.kind !== "anomaly") return event.kind;
  const severity = (event.detail as { severity?: string } | null)?.severity;
  return severity === "error" ? "anomaly_error" : "anomaly_warn";
}

export function buildEventMarkers(
  events: TelemetryEvent[],
  opts: { fromMs: number; toMs: number },
): { markers: TimelineMarker[]; hidden: number } {
  const inWindow: TimelineMarker[] = [];

  for (const event of events) {
    if (!MARKER_KINDS.includes(event.kind)) continue;
    const tsMs = new Date(event.ts).getTime();
    if (!isFinite(tsMs) || tsMs < opts.fromMs || tsMs > opts.toMs) continue;
    inWindow.push({
      key: `${event.kind}-${event.ts}-${event.ref?.src_line ?? inWindow.length}`,
      kind: event.kind,
      tone: markerTone(event),
      tsMs,
      event,
    });
  }

  inWindow.sort((a, b) => a.tsMs - b.tsMs);
  return {
    markers: inWindow.slice(0, MAX_MARKERS),
    hidden: Math.max(0, inWindow.length - MAX_MARKERS),
  };
}

/* ------------------------------------------------------------------ */
/*  View                                                               */
/* ------------------------------------------------------------------ */

function tickLabel(ms: number, tz: TelemetryTz, spanMs: number): string {
  const date = new Date(ms);
  const withDate = spanMs > 24 * 3_600_000;
  if (tz === "utc") {
    const iso = date.toISOString();
    return withDate ? `${iso.slice(5, 10)} ${iso.slice(11, 16)}` : iso.slice(11, 16);
  }
  return formatInstant(ms, { timeZone: viewerTimeZone(), preset: withDate ? "monthDayTime" : "time" });
}

function segmentDetailText(t: Translate, detail: TimelineSegmentDetail): string {
  if (detail.kind === "gap") {
    return t("operator.telemetry.segment_gap", {
      duration: formatSeconds(detail.seconds),
      threshold: formatSeconds(detail.offlineThresholdS),
    });
  }
  const built = t("operator.telemetry.segment_built", { buckets: detail.buckets, samples: detail.samples });
  return detail.states.length
    ? t("operator.telemetry.segment_reported", { built, states: detail.states.join(", ") })
    : built;
}

function segmentTitle(t: Translate, segment: TimelineSegment, tz: TelemetryTz): string {
  return [
    familyLabel(t, segment.family),
    t("operator.telemetry.segment_range", {
      start: formatTs(new Date(segment.startMs).toISOString(), tz),
      end: formatTs(new Date(segment.endMs).toISOString(), tz),
    }),
    t("operator.telemetry.segment_duration", { duration: formatSeconds((segment.endMs - segment.startMs) / 1000) }),
    segmentDetailText(t, segment.detail),
  ].join("\n");
}

/** A click hands back a range, never a point — and never a range of zero width. */
function rangeOf(startMs: number, endMs: number): { from: string; to: string } {
  if (endMs - startMs >= MIN_RANGE_MS) {
    return { from: toIsoZ(new Date(startMs)), to: toIsoZ(new Date(endMs)) };
  }
  const centre = (startMs + endMs) / 2;
  return {
    from: toIsoZ(new Date(centre - MIN_RANGE_MS / 2)),
    to: toIsoZ(new Date(centre + MIN_RANGE_MS / 2)),
  };
}

export function TimelineMap({
  from,
  to,
  series,
  events,
  bucketSeconds,
  tz,
  onSelectRange,
  onSelectEvent,
}: {
  from: string;
  to: string;
  series: SeriesPoint[];
  events: TelemetryEvent[];
  bucketSeconds?: number;
  tz: TelemetryTz;
  /** Narrows the analysis window — the only thing on this component that can cost a scan. */
  onSelectRange?: (range: { from: string; to: string }) => void;
  onSelectEvent?: (event: TelemetryEvent) => void;
}) {
  const { t } = useCatalog();
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  const spanMs = toMs - fromMs;

  const built = React.useMemo(() => {
    if (!isFinite(fromMs) || !isFinite(toMs) || spanMs <= 0) {
      return { bands: [] as TimelineSegment[], markers: [] as TimelineMarker[], hiddenMarkers: 0 };
    }
    const win = { fromMs, toMs };
    const { markers, hidden } = buildEventMarkers(events, win);
    return {
      // ONE chronological order, for bands and markers alike. Which element
      // paints over which is `z-index`'s job (see BAND_LAYER / MARKER_STYLE.layer);
      // reordering the DOM to get a gap on top would buy that at the price of a
      // tab sequence — and a reading order — that jumps backwards in time.
      bands: [
        ...buildStateSegments(series, { ...win, bucketSeconds }),
        ...buildGapSegments(events, win),
      ].sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs),
      markers,
      hiddenMarkers: hidden,
    };
  }, [fromMs, toMs, spanMs, series, events, bucketSeconds]);

  if (!isFinite(fromMs) || !isFinite(toMs) || spanMs <= 0) {
    return <div className="text-sm text-muted-foreground">{t("operator.telemetry.no_window")}</div>;
  }

  const { bands, markers, hiddenMarkers } = built;
  const empty = !bands.length && !markers.length;

  const left = (ms: number) => `${((ms - fromMs) / spanMs) * 100}%`;
  const width = (startMs: number, endMs: number) => `${((endMs - startMs) / spanMs) * 100}%`;
  const ticks = Array.from({ length: 5 }, (_, index) => fromMs + (spanMs * index) / 4);

  const presentFamilies = new Set<TimelineStateFamily>(bands.map((band) => band.family));
  const presentTones = new Set(markers.map((m) => m.tone));

  if (empty) {
    return (
      <div data-testid="telemetry-timeline" className="text-sm text-muted-foreground">
        {t("operator.telemetry.timeline_empty")}
      </div>
    );
  }

  return (
    <div data-testid="telemetry-timeline" className="space-y-2">
      <div>
        <div
          data-testid="timeline-scale"
          className="relative h-8 w-full overflow-hidden rounded-[3px] bg-muted/40"
        >
          {bands.map((band) => {
            const label = familyLabel(t, band.family);
            const title = segmentTitle(t, band, tz);
            const pct = ((band.endMs - band.startMs) / spanMs) * 100;
            return (
              <button
                key={band.key}
                type="button"
                title={title}
                aria-label={title}
                data-testid={`timeline-state-${band.key}`}
                data-family={band.family}
                disabled={!onSelectRange}
                onClick={
                  onSelectRange ? () => onSelectRange(rangeOf(band.startMs, band.endMs)) : undefined
                }
                style={{
                  left: left(band.startMs),
                  width: width(band.startMs, band.endMs),
                  zIndex: BAND_LAYER[band.family] ?? 1,
                }}
                className={`absolute inset-y-0 min-w-[2px] overflow-hidden whitespace-nowrap px-1 text-left text-[10px] font-medium transition-opacity hover:opacity-75 ${FAMILY_STYLE[band.family].block} ${FAMILY_STYLE[band.family].text}`}
              >
                {pct >= 12 ? label : null}
              </button>
            );
          })}

          {markers.map((marker) => {
            const style = MARKER_STYLE[marker.tone];
            const title = [
              t("operator.telemetry.marker", { tone: marker.tone, raw: marker.kind }),
              formatTs(marker.event.ts, tz),
              marker.event.state ? t("operator.telemetry.marker_state", { state: marker.event.state }) : null,
              eventDetailText(t, marker.event),
            ]
              .filter((part) => part && part !== "—")
              .join("\n");
            return (
              <button
                key={marker.key}
                type="button"
                title={title}
                aria-label={title}
                data-testid={`timeline-event-${marker.key}`}
                data-kind={marker.kind}
                data-tone={marker.tone}
                disabled={!onSelectEvent}
                onClick={onSelectEvent ? () => onSelectEvent(marker.event) : undefined}
                style={{ left: left(marker.tsMs), zIndex: 10 + (style?.layer ?? 0) }}
                className={`absolute inset-y-0 -translate-x-1/2 rounded-[1px] ${style?.width ?? "w-[2px]"} ${style?.block ?? "bg-foreground/60"}`}
              />
            );
          })}
        </div>

        <div data-testid="timeline-ticks" className="relative h-4">
          {ticks.map((ms, index) => (
            <span
              key={ms}
              className="absolute top-0 text-[10px] tabular-nums text-muted-foreground"
              style={{
                left: left(ms),
                transform:
                  index === 0 ? "none" : index === ticks.length - 1 ? "translateX(-100%)" : "translateX(-50%)",
              }}
            >
              {tickLabel(ms, tz, spanMs)}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {(Object.keys(FAMILY_STYLE) as TimelineStateFamily[])
          .filter((family) => presentFamilies.has(family))
          .map((family) => (
            <span key={family} className="flex items-center gap-1.5">
              <span className={`inline-block h-2.5 w-2.5 rounded-[2px] ${FAMILY_STYLE[family].block}`} />
              {familyLabel(t, family)}
            </span>
          ))}
        {Object.keys(MARKER_STYLE)
          .filter((tone) => presentTones.has(tone))
          .map((tone) => (
            <span key={tone} className="flex items-center gap-1.5">
              <span className={`inline-block h-2.5 rounded-[1px] ${MARKER_STYLE[tone].width} ${MARKER_STYLE[tone].block}`} />
              {t("operator.telemetry.marker", { tone, raw: tone })}
            </span>
          ))}
      </div>

      {hiddenMarkers > 0 && (
        <div data-testid="timeline-markers-truncated" className="text-[11px] text-muted-foreground">
          {t("operator.telemetry.markers_hidden", { count: hiddenMarkers })}
        </div>
      )}

      <div className="text-[11px] text-muted-foreground">
        {t("operator.telemetry.timeline_note")}
      </div>
    </div>
  );
}
